import { Routes } from '@angular/router';
import { authGuard } from '@rekruto/core';
import { companySeatGuard, noSeatGuard, signedOutGuard } from './lib/company-guards';

/**
 * The company app routes (docs/11 information architecture): the six tenant-scoped surfaces plus
 * the position wizard and the candidate detail, and the tenant onboarding surfaces a new company
 * needs.
 *
 * The routed shell carries `authGuard` (session) and `companySeatGuard` (tenant seat), so a
 * signed-in user without a company lands on onboarding instead of an empty dashboard. `/login`,
 * `/register` and `/verify-email` are siblings of the shell and render standalone. `/onboarding`
 * needs a session but explicitly not a seat.
 *
 * The `/auth/*` redirects keep the marketing site's already-published URLs working; the canonical
 * routes are the flat ones (`/login`, `/register`).
 */
export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'overblik' },
  {
    path: 'login',
    canActivate: [signedOutGuard],
    loadComponent: () => import('./pages/login/login-page').then(m => m.LoginPage),
    title: 'Log ind',
  },
  {
    path: 'register',
    canActivate: [signedOutGuard],
    loadComponent: () => import('./pages/register/register-page').then(m => m.RegisterPage),
    title: 'Opret konto',
  },
  { path: 'auth/login', redirectTo: 'login' },
  { path: 'auth/register', redirectTo: 'register' },
  {
    path: 'verify-email',
    loadComponent: () => import('./pages/verify-email/verify-email-page').then(m => m.VerifyEmailPage),
    title: 'Bekræft e-mail',
  },
  {
    path: 'onboarding',
    canActivate: [authGuard, noSeatGuard],
    loadComponent: () => import('./pages/onboarding/onboarding-page').then(m => m.OnboardingPage),
    title: 'Opret virksomhed',
  },
  {
    path: '',
    canActivate: [authGuard, companySeatGuard],
    loadComponent: () => import('./shell/shell.component').then(m => m.ShellComponent),
    children: [
      {
        path: 'overblik',
        loadComponent: () => import('./pages/overblik/overblik-page').then(m => m.OverblikPage),
        title: 'Overblik',
      },
      {
        path: 'stillinger',
        loadComponent: () => import('./pages/stillinger/stillinger-page').then(m => m.StillingerPage),
        title: 'Stillinger',
      },
      {
        path: 'stillinger/ny',
        loadComponent: () =>
          import('./pages/stillinger/stillinger-wizard-page').then(m => m.StillingerWizardPage),
        title: 'Ny stilling',
      },
      {
        path: 'opret-ai',
        loadComponent: () => import('./pages/opret-ai/opret-ai-page').then(m => m.OpretAiPage),
        title: 'Opret stilling med AI',
      },
      {
        path: 'kandidater',
        loadComponent: () => import('./pages/kandidater/kandidater-page').then(m => m.KandidaterPage),
        title: 'Kandidater',
      },
      {
        path: 'kandidater/:id',
        loadComponent: () =>
          import('./pages/kandidater/kandidat-detail-page').then(m => m.KandidatDetailPage),
        title: 'Kandidat',
      },
      {
        path: 'rubrikker',
        loadComponent: () => import('./pages/rubrikker/rubrikker-page').then(m => m.RubrikkerPage),
        title: 'Rubrikker & skills',
      },
      {
        path: 'integrationer',
        loadComponent: () =>
          import('./pages/integrationer/integrationer-page').then(m => m.IntegrationerPage),
        title: 'Integrationer',
      },
      {
        path: 'indstillinger',
        loadComponent: () =>
          import('./pages/indstillinger/indstillinger-page').then(m => m.IndstillingerPage),
        title: 'Indstillinger',
      },
    ],
  },
  { path: '**', redirectTo: 'overblik' },
];
